"use client";
import type { GPOnboardingStepProps } from "./utils";

import { AppleLogo } from "@/components/icons/AppleLogo";
import { GoogleLogo } from "@/components/icons/GoogleLogo";
import { ImmutableLogo } from "@/components/icons/ImmutableLogo";
import { Button } from "@/components/ui/button";
import { usePassportProvider } from "@/context";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import { useLoginModal } from "@/context/LoginModalContext";
import { cn } from "@/lib/utils";
import { notifyError } from "@/utils/monitoring";
import { LoaderCircle, MailIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { GlassButton } from "../GlassButton";

export const GPOnboardingLogin = ({
  onNext,
  isVisible,
  track,
}: GPOnboardingStepProps) => {
  const loginProgressedRef = useRef(false);
  const [loginMethod, setLoginMethod] = useState<
    "google" | "apple" | "email" | null
  >(null);

  const { loginDirectly, login, isLoggingIn, isLoggedIn } =
    usePassportProvider();
  const { setDisableLoadingModal } = useLoginModal();

  const handleSocialLogin = async (method: "google" | "apple") => {
    track({
      action: Action.PRESSED,
      extras: {
        control: `social-login-${method}`,
        controlLabel: `Sign in with ${method}`,
        controlType: ControlType.BUTTON,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
    setDisableLoadingModal(true);
    setLoginMethod(method);
    try {
      await loginDirectly(method);
      track({
        action: Action.SUCCEEDED,
        extras: {
          control: `social-login-${method}`,
          section: GamePageSection.ONBOARDING_MODAL,
        },
      });
    } catch (error) {
      track({
        action: Action.FAILED,
        extras: {
          control: `social-login-${method}`,
          section: GamePageSection.ONBOARDING_MODAL,
        },
      });
      notifyError(error, "login");
    } finally {
      setDisableLoadingModal(false);
    }
  };

  const handleEmailLogin = async () => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "email-login",
        controlLabel: "Sign in with email",
        controlType: ControlType.BUTTON,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
    setDisableLoadingModal(true);
    setLoginMethod("email");
    try {
      await login();
      track({
        action: Action.SUCCEEDED,
        extras: {
          control: "email-login",
          section: GamePageSection.ONBOARDING_MODAL,
        },
      });
    } catch (error) {
      track({
        action: Action.FAILED,
        extras: {
          control: "email-login",
          section: GamePageSection.ONBOARDING_MODAL,
        },
      });
      notifyError(error, "login");
    } finally {
      setDisableLoadingModal(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn || !isVisible || loginProgressedRef.current) return;
    const timeout = setTimeout(() => {
      loginProgressedRef.current = true;
      onNext();
    }, 1000);
    return () => clearTimeout(timeout);
  }, [isLoggedIn, onNext, isVisible]);

  const handleRulesClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "rules-privacy",
        controlLabel: "Official Rules & Data Privacy",
        controlType: ControlType.BUTTON,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    window.open("https://www.immutable.com/terms", "_blank");
  }, [track]);

  return (
    <div className="flex-grow flex flex-col items-center justify-between">
      {/* Progress bar with Motivation text */}
      <div>
        <p className="text-[clamp(1.25rem,6.25vw,1.875rem)] font-bold leading-[clamp(1.5rem,6.25vw,2.25rem)]">
          Become a Pool Champion
        </p>
      </div>
      <div className="relative size-full">
        {/* Logging in */}
        <div
          className={cn(
            "absolute top-1/2 left-1/2 -translate-x-1/2 px-6 py-10 space-y-8 flex flex-col items-center transition-all duration-200 ease-in-out",
            isLoggingIn || isLoggedIn
              ? "opacity-100 -translate-y-1/2 pointer-events-auto"
              : "opacity-0 translate-y-10 pointer-events-none",
          )}
        >
          <LoaderCircle className="size-6 animate-spin" />
          <div className="text-center space-y-2">
            <p className="font-bold">Signing in</p>
            <p className="text-sm text-muted-foreground whitespace-nowrap">
              Continue on <span className="capitalize">{loginMethod}</span>,
              then return.
            </p>
          </div>
        </div>
        {/* Sign in Options */}
        <div
          className={cn(
            "absolute top-1/2 left-1/2 -translate-x-1/2 space-y-5 transition-all duration-200 ease-in-out",
            isLoggingIn || isLoggedIn
              ? "opacity-0 translate-y-10 pointer-events-none"
              : "opacity-100 -translate-y-1/2 pointer-events-auto",
          )}
        >
          <div className="flex items-center gap-x-5">
            <div className="h-px bg-neutral-50/20 flex-grow" />
            <p className="text-sm text-neutral-50">Sign in with Immutable</p>
            <div className="h-px bg-neutral-50/20 flex-grow" />
          </div>

          <div className="flex gap-x-5 md:gap-x-10">
            <GlassButton
              className="size-12 md:size-16"
              onClick={() => handleSocialLogin("google")}
            >
              <GoogleLogo className="size-4 md:size-5" />
            </GlassButton>
            <GlassButton
              className="size-12 md:size-16"
              onClick={() => handleSocialLogin("apple")}
            >
              <AppleLogo className="size-4 md:size-5" />
            </GlassButton>
            <GlassButton
              className="size-12 md:size-16"
              onClick={() => handleEmailLogin()}
            >
              <MailIcon className="size-4 md:size-5" />
            </GlassButton>
          </div>
        </div>
      </div>
      {/* Rules & Privacy */}
      <Button
        variant="ghost"
        leftIcon={
          <ImmutableLogo className="height-full aspect-square" fill="white" />
        }
        className="text-sm text-muted-foreground"
        onClick={handleRulesClick}
      >
        Official Rules & Data Privacy
      </Button>
    </div>
  );
};
